import { memo, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell, CheckCheck, Search, Settings2, Mail, MailX,
  Clock, AlertTriangle, Flame, CheckCircle2, FolderKanban,
  MessageSquare, ClipboardCheck, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { WorkflowNotification } from '@/types/api';

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'escalation', label: 'Escalations' },
  { value: 'task_assigned', label: 'Assigned' },
  { value: 'task_overdue', label: 'Overdue' },
  { value: 'task_due_soon', label: 'Due Soon' },
  { value: 'attestation_required', label: 'Attestations' },
  { value: 'project_created', label: 'Projects' },
] as const;

const TYPE_META: Record<string, { icon: typeof Bell; color: string; label: string }> = {
  escalation: { icon: Flame, color: 'text-compliance-critical', label: 'Escalation' },
  task_assigned: { icon: UserPlus, color: 'text-teal', label: 'Task Assigned' },
  task_overdue: { icon: AlertTriangle, color: 'text-compliance-critical', label: 'Overdue' },
  task_due_soon: { icon: Clock, color: 'text-compliance-medium', label: 'Due Soon' },
  attestation_required: { icon: ClipboardCheck, color: 'text-purple-500', label: 'Attestation' },
  project_created: { icon: FolderKanban, color: 'text-teal', label: 'New Project' },
  checklist_complete: { icon: CheckCircle2, color: 'text-compliance-low', label: 'Checklist Done' },
  comment: { icon: MessageSquare, color: 'text-muted-foreground', label: 'Comment' },
};

const EMAIL_TYPE_OPTIONS = [
  { value: 'escalation', label: 'Escalations' },
  { value: 'task_overdue', label: 'Overdue Tasks' },
  { value: 'attestation_required', label: 'Attestation Required' },
  { value: 'task_assigned', label: 'Task Assigned' },
  { value: 'task_due_soon', label: 'Due Soon Reminders' },
  { value: 'project_created', label: 'New Projects' },
] as const;

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function NotificationsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get('type') ?? 'all';
  const readFilter = (searchParams.get('unread') === 'true' ? 'unread' : 'all') as 'all' | 'unread';
  const searchQuery = searchParams.get('q') ?? '';
  const [showPreferences, setShowPreferences] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30);

  const setTypeFilter = useCallback((v: string) => {
    setSearchParams((prev) => { prev.set('type', v); return prev; });
  }, [setSearchParams]);

  const toggleUnread = useCallback(() => {
    setSearchParams((prev) => {
      if (prev.get('unread') === 'true') prev.delete('unread');
      else prev.set('unread', 'true');
      return prev;
    });
  }, [setSearchParams]);

  const setSearch = useCallback((v: string) => {
    setSearchParams((prev) => { if (v) prev.set('q', v); else prev.delete('q'); return prev; });
  }, [setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-all', typeFilter],
    queryFn: () => api.notifications.list({
      limit: 100,
      ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
    }),
    enabled: !!user,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: countData } = useQuery({
    queryKey: ['notification-unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    staleTime: 15_000,
    refetchInterval: 30_000,
    enabled: !!user,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications-all', typeFilter] });
      const prev = queryClient.getQueryData<{ notifications: WorkflowNotification[] }>(['notifications-all', typeFilter]);
      queryClient.setQueryData(['notifications-all', typeFilter], (old: { notifications: WorkflowNotification[] } | undefined) => ({
        notifications: (old?.notifications ?? []).map((n) =>
          n.notificationId === id ? { ...n, isRead: true } : n,
        ),
      }));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['notifications-all', typeFilter], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications-all', typeFilter] });
      const prev = queryClient.getQueryData<{ notifications: WorkflowNotification[] }>(['notifications-all', typeFilter]);
      queryClient.setQueryData(['notifications-all', typeFilter], (old: { notifications: WorkflowNotification[] } | undefined) => ({
        notifications: (old?.notifications ?? []).map((n) => ({ ...n, isRead: true })),
      }));
      queryClient.setQueryData(['notification-unread-count'], { count: 0 });
      return { prev };
    },
    onSuccess: () => toast.success('All notifications marked as read'),
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['notifications-all', typeFilter], ctx.prev);
      toast.error('Failed to mark all as read');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-all'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
    },
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = countData?.count ?? 0;

  const filtered = notifications.filter((n) => {
    if (readFilter === 'unread' && n.isRead) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return n.title.toLowerCase().includes(q) || (n.body?.toLowerCase().includes(q) ?? false);
    }
    return true;
  });

  const handleClick = useCallback((n: WorkflowNotification) => {
    if (!n.isRead) markReadMutation.mutate(n.notificationId);
    if (n.link) navigate(n.link);
  }, [markReadMutation, navigate]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Notifications"
        subtitle={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
        actions={
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => markAllReadMutation.mutate()} disabled={markAllReadMutation.isPending} aria-label="Mark all notifications as read">
                <CheckCheck className="mr-1.5 h-3.5 w-3.5" />Mark All Read
              </Button>
            )}
            <Button variant={showPreferences ? 'default' : 'outline'} size="sm" onClick={() => setShowPreferences(!showPreferences)} aria-label="Toggle notification preferences">
              <Settings2 className="mr-1.5 h-3.5 w-3.5" />Preferences
            </Button>
          </div>
        }
      />

      {showPreferences && <PreferencesPanel />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v ?? 'all')}>
          <TabsList variant="line">
            {TYPE_FILTERS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="ml-auto flex items-center gap-2">
          <Button variant={readFilter === 'unread' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={toggleUnread}>
            {readFilter === 'unread' ? 'Showing Unread' : 'Show Unread Only'}
          </Button>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search notifications..." value={searchQuery} onChange={(e) => setSearch(e.target.value)} className="h-7 pl-8 text-sm" />
          </div>
        </div>
      </div>

      {/* Notification list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="rounded-lg border bg-card p-3">
              <div className="flex items-start gap-3">
                <div className="h-4 w-4 animate-pulse rounded bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={readFilter === 'unread' ? CheckCheck : Bell}
          title={readFilter === 'unread' ? 'No unread notifications' : searchQuery ? 'No matching notifications' : 'No notifications yet'}
          description={readFilter === 'unread' ? "You're all caught up!" : searchQuery ? 'Try adjusting your search.' : 'Notifications will appear here when events occur.'}
        />
      ) : (
        <div className="space-y-1">
          {filtered.slice(0, visibleCount).map((n) => (
            <NotificationRow key={n.notificationId} notification={n} onClick={handleClick} />
          ))}
          {visibleCount < filtered.length && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">Showing {visibleCount} of {filtered.length}</p>
              <Button variant="outline" size="sm" onClick={() => setVisibleCount((p) => p + 30)}>Load More</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const NotificationRow = memo(function NotificationRow({
  notification,
  onClick,
}: {
  readonly notification: WorkflowNotification;
  readonly onClick: (n: WorkflowNotification) => void;
}) {
  const meta = TYPE_META[notification.type] ?? { icon: Bell, color: 'text-muted-foreground', label: notification.type };
  const Icon = meta.icon;

  return (
    <button
      type="button"
      className="w-full text-left"
      onClick={() => onClick(notification)}
      aria-label={`${notification.isRead ? '' : 'Unread: '}${notification.title}`}
    >
      <Card className={cn(
        'cursor-pointer transition-colors hover:bg-muted/50',
        !notification.isRead && 'border-l-2 border-l-teal bg-teal/5',
      )}>
        <CardContent className="flex items-start gap-3 p-3">
          <div className={cn('mt-0.5 shrink-0', meta.color)}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={cn('text-sm', !notification.isRead && 'font-medium')}>{notification.title}</p>
              <Badge variant="outline" className="text-[9px] shrink-0">{meta.label}</Badge>
            </div>
            {notification.body && <p className="mt-0.5 truncate text-xs text-muted-foreground">{notification.body}</p>}
            <p className="mt-0.5 text-[10px] text-muted-foreground">{formatRelativeTime(notification.createdAt)}</p>
          </div>
          {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal" />}
        </CardContent>
      </Card>
    </button>
  );
});

function PreferencesPanel() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.notifications.preferences(),
    enabled: !!user,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Record<string, unknown>) => api.notifications.updatePreferences(updates),
    onSuccess: () => {
      toast.success('Preferences updated');
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: () => toast.error('Failed to update preferences'),
  });

  const prefs = data?.preferences;
  if (isLoading || !prefs) {
    return <Card><CardContent className="p-4"><div className="h-24 animate-pulse rounded bg-muted" /></CardContent></Card>;
  }

  const toggleEmailType = (type: string) => {
    const current = [...prefs.emailTypes];
    const idx = current.indexOf(type);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(type);
    updateMutation.mutate({ emailTypes: current });
  };

  return (
    <Card className="border-teal/30">
      <CardContent className="p-4">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Notification Preferences</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              {prefs.emailEnabled ? <Mail className="h-4 w-4 text-teal" /> : <MailX className="h-4 w-4 text-muted-foreground" />}
              <div>
                <p className="text-sm font-medium">Email Notifications</p>
                <p className="text-[10px] text-muted-foreground">Receive email for critical alerts</p>
              </div>
            </div>
            <Switch checked={prefs.emailEnabled} onCheckedChange={(checked) => updateMutation.mutate({ emailEnabled: checked })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-teal" />
              <div>
                <p className="text-sm font-medium">In-App Notifications</p>
                <p className="text-[10px] text-muted-foreground">Show notifications in the app</p>
              </div>
            </div>
            <Switch checked={prefs.inAppEnabled} onCheckedChange={(checked) => updateMutation.mutate({ inAppEnabled: checked })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-teal" />
              <div>
                <p className="text-sm font-medium">Digest Frequency</p>
                <p className="text-[10px] text-muted-foreground">How often to batch emails</p>
              </div>
            </div>
            <Select value={prefs.digestFrequency} onValueChange={(v) => updateMutation.mutate({ digestFrequency: v ?? 'realtime' })}>
              <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">Realtime</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {prefs.emailEnabled && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Email me about:</p>
            <div className="flex flex-wrap gap-2">
              {EMAIL_TYPE_OPTIONS.map((opt) => {
                const active = prefs.emailTypes.includes(opt.value);
                return (
                  <button key={opt.value} type="button" className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors', active ? 'border-teal/50 bg-teal/10 text-teal-light' : 'border-border text-muted-foreground hover:border-teal/30')} onClick={() => toggleEmailType(opt.value)}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
